import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { mapProductsToItemData } from '@/shared/api/mappers/product';
import { useGetNicheSoldProductsQuery } from '@/shared/hooks/queries/generated';
import type { ProductItemData } from '@/shared/types/product';

interface UseNicheSoldProductsParams {
  analysisId: string;
  brandOrFullPath: string;
  enabled?: boolean;
}

interface UseNicheSoldProductsReturn {
  products: ProductItemData[];
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export const useNicheSoldProducts = ({
  analysisId,
  brandOrFullPath,
  enabled = true,
}: UseNicheSoldProductsParams): UseNicheSoldProductsReturn => {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['nicheSoldProducts', analysisId, brandOrFullPath],
    queryFn: () =>
      useGetNicheSoldProductsQuery.fetcher({
        analysisId,
        brandOrFullPath,
      })(),
    enabled: enabled && !!analysisId && !!brandOrFullPath,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    staleTime: 0,
  });

  const products = useMemo(() => {
    return mapProductsToItemData(data?.getNicheSoldProducts ?? []);
  }, [data]);

  return {
    products,
    isLoading,
    error: error ? 'Erreur lors de la récupération des produits vendus' : null,
    refetch,
  };
};
